<script lang="ts">
	import type { ClassValue } from 'svelte/elements';
	import type { SpecCardType, SpecCardTypeType, SpecCardTypeWithBlock } from './types';
	import { ItineraryTrafficMode } from '$houdini';
	import AlertBox from '$lib/components/atoms/alertBox/AlertBox.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import ObfuscatedLink from '$lib/components/atoms/obfuscatedLink/ObfuscatedLink.svelte';
	import SkeletonBlock from '$lib/components/atoms/skeleton/SkeletonBlock.svelte';
	import FavoriteCard from '$lib/components/route/routeSpecCard/components/FavoriteCard.svelte';
	import RouteGridCard from '$lib/components/route/routeSpecCard/components/RouteGridCard.svelte';
	import { ROUTE_RESULTS } from '$lib/constants/route';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';

	interface Props {
		isLoading?: boolean;
		cards?: Record<SpecCardTypeType, SpecCardType | undefined>;
		onClick?: (type: SpecCardTypeType) => void;
		onBlablacarMount?: () => void;
	}

	const {
		isLoading = false,
		cards,
		onClick,
		onBlablacarMount,
	}: Props = $props();

	const searchParams = getSearchParamsStore();
	let isBlablacarMounted = false;
	const CARDS_LIST_ORDER = [
		{ type: 'cost', order: 1 },
		{ type: 'blablacar', order: 2 },
		{ type: 'alert', order: 3 },
		{ type: 'roadsheet', order: 4 },
	];

	$effect(() => {
		if (cards?.blablacar && !isBlablacarMounted) {
			onBlablacarMount?.();
			isBlablacarMounted = true;
		}
	});

	function getOrder(key: string, baseOrder: number) {
		const hasAlert = cards?.alert?.title !== undefined;
		const hasBlablacar = cards?.blablacar?.title !== undefined;

		if (key === 'alert') {
			return hasBlablacar ? baseOrder : 999;
		}

		if (key === 'roadsheet') {
			if (hasBlablacar && hasAlert) return 4;
			if ((hasBlablacar && !hasAlert) || (!hasBlablacar && hasAlert)) return 3;
			return baseOrder;
		}

		return baseOrder;
	};

	function defineCardListOrder(cards: Record<SpecCardTypeType, SpecCardType | undefined>): SpecCardTypeWithBlock[] {
		if (!cards) return [];

		const list = [
			{
				...cards.cost,
				type: 'cost',
				blockName: costCard,
			},
			{
				...cards.blablacar,
				type: 'blablacar',
				blockName: blablacarCard,
			},
			{
				...cards.alert,
				type: 'alert',
				blockName: alertCard,
			},
			{
				...cards.roadsheet,
				type: 'roadsheet',
				blockName: roadsheetCard,
			},
		];

		const listOrdered = CARDS_LIST_ORDER
			.map(({ type, order }) => ({ type, order: getOrder(type as SpecCardTypeType, order) }))
			.sort((a, b) => a.order - b.order);

		return list
			.filter(
				(card): card is SpecCardTypeWithBlock =>
					Boolean(card?.title) && typeof card.type !== 'undefined',
			)
			.sort(
				(a, b) =>
					listOrdered.findIndex(c => c.type === a.type)
						- listOrdered.findIndex(c => c.type === b.type),
			);
	}
</script>

{#if isLoading}
	{@render skeleton()}
{:else}
	{#if cards}
		<RouteGridCard
			dataTestId="route-spec-card"
			list={defineCardListOrder(cards)}
		>
			<FavoriteCard />
		</RouteGridCard>
	{/if}
{/if}

{#snippet alertCard(item: SpecCardType)}
	<AlertBox
		color="amber"
		title={item.title ?? undefined}
		text={item.description!}
		dataTestId="route-spec-card-alert-card"
		class="w-full text-coloredneutral"
		contentClass="justify-start"
		buttonIcon="east"
		buttonText={m.traffic_deviation__cta()}
		onclick={() => {
			gotoWithParams(ROUTE_RESULTS, {
				...$searchParams,
				selectedRoute: $searchParams.selectedRoute ?? 0,
				traffic: ItineraryTrafficMode.NONE,
			});
		}}
	/>
{/snippet}

{#snippet costCard(item: SpecCardType)}
	{@render cardTitle(item.title, undefined, 'cost-title')}

	{@render definitions(item.definitions, 'cost-definitions')}

	{@render bouton('cost', item.buttonLabel, undefined, 'cost-cta')}
{/snippet}
{#snippet blablacarCard(item: SpecCardType)}
	{@render cardTitle(item.title, 'm-0!', 'blablacar-title')}
	{#if item.subtitle}
		<p class="m-0 flex items-baseline">{item.subtitle} {#if item.src && item.alt && item.srcset} <Image class="ml-1" src={item.src} alt={item.alt} srcset={item.srcset} height="20px" />{/if}</p>
	{/if}
	{#if item.description}
		<p class="text-2.5 text-coloredneutral-softest my-2">{item.description}</p>
	{/if}
	{@render blablacarButton(item)}
{/snippet}
{#snippet roadsheetCard(item: SpecCardType)}
	{@render cardTitle(item.title, 'roadsheet-title', 'roadsheet-title')}
	{@render definitions(item.definitions, 'roadsheet-definitions')}
	<div>
		{@render bouton('roadsheet', item.buttonLabel, undefined, 'roadsheet-cta')}
	</div>
{/snippet}
{#snippet cardTitle(title?: string, classes?: ClassValue, dataTestId?: string)}
	<p class={['font-size-4 mt-0 mb-2', classes]} data-testid={dataTestId}>
		{#if title?.includes('<b>')}
			{title.split('<b>')[0]}<b>{title.split('<b>')[1].split('</b>')[0]}</b>{title.split('</b>')[1]}
		{:else}
			{title}
		{/if}
	</p>
{/snippet}
{#snippet bouton(type: SpecCardTypeType, text?: string, icon = 'east', dataTestId?: string)}
	<Button
		class="p-x-0 p-y-2.5 border-primary-weak rounded-sm text-primary"
		labelClasses="p-0!"
		variant="ghost"
		size="sm"
		icon={icon}
		text={text}
		iconPosition="right"
		onclick={() => { onClick?.(type); }}
		dataTestId={dataTestId}
	/>
{/snippet}
{#snippet blablacarButton(item: SpecCardType)}
	<ObfuscatedLink href={item.href} target="_blank" rel="noopener noreferrer" dataTestId="blablacar-cta">
		{@render bouton('blablacar', item.buttonLabel, 'open-in-new')}
	</ObfuscatedLink>
{/snippet}

{#snippet definitions(definitions?: { label: string; value: string }[], dataTestId?: string)}
	{#if definitions}
		<div class="grid grid-cols-2 gap-2 w-full">
			{#each definitions as definition, index (definition.label)}
				<dl class="line-height-5 m-0 mb-2 definition-item" data-testid={`${dataTestId ? `${dataTestId}-${index}` : ''}`}>
					<dt class="font-size-3.5 truncate">{definition.label}</dt>
					<dd class="m-0 font-size-4 truncate">
						{definition.value}
						{#if definition.label === m.route_spec_card__roadsheet__emissions()}
							Kg <span class="font-size-3">CO<sub class="font-size-2.5 align-baseline">2</sub></span>
						{/if}
					</dd>
				</dl>
			{/each}
		</div>
	{/if}
{/snippet}

{#snippet skeleton()}
	<RouteGridCard list={[
		{
			title: ' ',
			type: 'cost',
			blockName: skeletonCard,
		},
		{
			title: ' ',
			type: 'blablacar',
			blockName: skeletonCard,
		},
		{
			title: ' ',
			type: 'roadsheet',
			blockName: skeletonLargeCard,
		},
	]}>
		<FavoriteCard disabled />
	</RouteGridCard>
{/snippet}
{#snippet skeletonCard()}
	<SkeletonBlock height="16px" width="100%" class="inline-block" />
	{@render skeletonDefinitions()}
	<SkeletonBlock height="28px" width="100px" class="inline-block" />
{/snippet}
{#snippet skeletonLargeCard()}
	<div class="grid grid-cols-3 gap-2 w-full">
		<div class="max-w-20">
			<SkeletonBlock height="14px" width="80px" class="inline-block" />
			<SkeletonBlock height="16px" width="40px" class="inline-block" />
		</div>
		<div class="max-w-20">
			<SkeletonBlock height="14px" width="60px" class="inline-block" />
			<SkeletonBlock height="16px" width="80px" class="inline-block" />
		</div>
		<div class="items-center flex justify-end">
			<SkeletonBlock height="28px" width="100px" class="inline-block" />
		</div>
	</div>
{/snippet}
{#snippet skeletonDefinitions()}
	<div class="grid grid-cols-2 gap-2">
		{#each [1, 2] as _, index (index)}
			{@render skeletonDefinition()}
		{/each}
	</div>
{/snippet}
{#snippet skeletonDefinition()}
	<div class="line-height-5">
		<SkeletonBlock height="14px" width="60px" class="inline-block" />
		<SkeletonBlock height="16px" width="40px" class="inline-block" />
	</div>
{/snippet}
